import React, { useEffect, useState } from 'react';
import { X, Wallet } from 'lucide-react';
import Button from '../common/Button.js';
import Alert from '../common/Alert';
import cashbookService from '../../services/CashbookService.js';

const getToday = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const CashbookOpeningBalanceModal = ({ open, onClose, onSaved, locationHubId, ownerUserId }) => {
  const [openingCash, setOpeningCash] = useState('');
  const [openingUpi, setOpeningUpi] = useState('');
  const [asOfDate, setAsOfDate] = useState(getToday());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    cashbookService
      .getOpeningBalance(locationHubId, ownerUserId)
      .then((res) => {
        if (!active) return;
        const data = res.data?.data;
        if (data) {
          setOpeningCash(String(data.opening_cash ?? ''));
          setOpeningUpi(String(data.opening_upi ?? data.opening_bank ?? ''));
          setAsOfDate(data.as_of_date || getToday());
        } else {
          setOpeningCash('');
          setOpeningUpi('');
          setAsOfDate(getToday());
        }
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, locationHubId, ownerUserId]);

  if (!open) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const opening_cash = parseFloat(openingCash) || 0;
      const opening_upi = parseFloat(openingUpi) || 0;
      await cashbookService.setOpeningBalance({
        opening_cash,
        opening_upi,
        opening_bank: opening_upi,
        as_of_date: asOfDate,
        ...(locationHubId ? { location_hub_id: Number(locationHubId) } : {}),
        ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
      });
      Alert.success('Opening balance saved');
      onSaved?.();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to save opening balance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-buttonBg" /> Opening Balance
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-4">
          <p className="text-xs text-gray-500">
            Optional starting Cash and UPI. Leave blank to keep working from zero.
            If entries already exist, this amount is added on top of them.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opening Cash (₹)</label>
              <input
                type="number" step="0.01" value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                placeholder="0.00" disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opening UPI (₹)</label>
              <input
                type="number" step="0.01" value={openingUpi}
                onChange={(e) => setOpeningUpi(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                placeholder="0.00" disabled={loading}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
            <input
              type="date" value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              disabled={loading}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" loading={saving} disabled={saving || loading}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CashbookOpeningBalanceModal;
